/**
 * Shared contact / book-a-session form (Turnstile + compact fields)
 */

import React, { useState } from 'react'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { useTurnstile } from '../hooks/useTurnstile'
import TurnstileWidget from './TurnstileWidget'
import MarketingConsentField from './MarketingConsentField'
import { TURNSTILE_ACTIONS } from '../constants/turnstile'
import { withTurnstileToken } from '../utils/scheduler/turnstile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export default function ContactForm({
  source = 'contact_page',
  showHeading = true,
  fieldIdPrefix = '',
}) {
  const t = useTranslation()
  const { language } = useLanguage()
  const turnstile = useTurnstile()

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })
  const [marketingConsent, setMarketingConsent] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const fieldId = (name) => `${fieldIdPrefix}${name}`

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (turnstile.isEnabled && !turnstile.token) {
      setError(t('intake.turnstile.required'))
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          withTurnstileToken(
            {
              ...formData,
              language,
              source,
              marketing_consent: marketingConsent,
            },
            turnstile.token
          )
        ),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) {
        const message =
          res.status === 403
            ? t('intake.turnstile.failed')
            : data.error || t('contact.form.error.message')
        throw new Error(message)
      }
      setSubmitted(true)
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      })
      setMarketingConsent(true)
    } catch (err) {
      setError(err.message || t('contact.form.error.message'))
      turnstile.reset()
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="py-4 text-center sm:py-6">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-foreground sm:text-2xl">{t('contact.form.success.title')}</h2>
        <p className="mx-auto mt-2 max-w-lg text-muted-foreground">{t('contact.form.success.message')}</p>
      </div>
    )
  }

  return (
    <>
      {showHeading ? (
        <>
          <h2 className="mb-2 text-2xl font-bold text-primary-darkGreen sm:text-3xl">
            {t('contact.form.title')}
          </h2>
          <p className="mb-6 text-muted-foreground">{t('contact.form.subtitle')}</p>
        </>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={fieldId('name')}>{t('contact.form.name')} *</Label>
            <Input
              type="text"
              id={fieldId('name')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              autoComplete="name"
              className="text-base"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={fieldId('phone')}>{t('contact.form.phone')}</Label>
            <Input
              type="tel"
              id={fieldId('phone')}
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              autoComplete="tel"
              className="text-base"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={fieldId('subject')}>{t('contact.form.subject')} *</Label>
            <Input
              type="text"
              id={fieldId('subject')}
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="text-base"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={fieldId('email')}>{t('contact.form.email')} *</Label>
            <Input
              type="email"
              id={fieldId('email')}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              autoComplete="email"
              className="text-base"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor={fieldId('message')}>{t('contact.form.message')} *</Label>
            <Textarea
              id={fieldId('message')}
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={4}
              className="text-base"
            />
          </div>
        </div>

        <MarketingConsentField
          id={fieldId('marketing-consent')}
          checked={marketingConsent}
          onChange={setMarketingConsent}
        />

        <div className="contact-turnstile-host" role="group" aria-labelledby={fieldId('turnstile-label')}>
          <p id={fieldId('turnstile-label')} className="mb-3 text-center text-base font-medium text-muted-foreground">
            {t('intake.turnstile.securityVerification')}
          </p>
          <TurnstileWidget
            action={TURNSTILE_ACTIONS.CONTACT_FORM}
            resetSignal={turnstile.resetSignal}
            onTokenChange={turnstile.setToken}
            onError={turnstile.clearToken}
          />
        </div>

        {error ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-base text-destructive" role="alert">
            {error}
          </div>
        ) : null}

        <div className="w-full border-t border-border pt-6 text-center">
          <Button
            type="submit"
            size="lg"
            disabled={submitting || !turnstile.canSubmit}
            className="bg-primary-darkGreen px-10 text-white hover:bg-primary-darkGreen/90"
          >
            {submitting ? t('contact.form.submitting') : t('contact.form.submit')}
          </Button>
        </div>
      </form>
    </>
  )
}
